import {
  Alert,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import CustomButton from "components/common/CustomButton";
import { useGetConnectionCredentialTypesByTypeQuery } from "features/integrationsBeta/connections";
import { useCreateSecretMutation } from "features/integrationsBeta/credentials";
import { useGetConnectionTypesQuery } from "features/integrationsBeta/platformEnums";
import {
  useCreateConnectionMutation,
  useCreateProviderMutation,
  useGetIntegrationProvidersQuery,
  useLazyGetConnectionConfigSchemaByTypeQuery,
} from "features/integrationsBeta/registration";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { useEffect, useMemo, useState } from "react";
import { formatApiError } from "utils/formatApiError";
import { humanizeKey } from "utils/humanizeKey";
import JsonCodeField from "./JsonCodeField";
import JsonSchemaForm from "./JsonSchemaForm";

type Json = Record<string, unknown>;

const NEW_PROVIDER_VALUE = "__new_provider__";

/**
 * Standalone "New connection" dialog for the Connections management view:
 * provider (or a new one), type, optional name, schema-driven config and the
 * adapter's required credentials in one go. Mirrors the inline create flow of
 * ``ConnectionBindingField`` (incl. the retry-without-duplicating semantics:
 * once the connection row exists, re-submitting only retries failed secrets).
 */
export default function ConnectionCreateDialog({
  open,
  onClose,
  clientId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  clientId: number;
  onCreated?: (connectionId: number) => void;
}) {
  const dispatch = useAppDispatch();

  const { data: providers = [] } = useGetIntegrationProvidersQuery();
  const { data: connectionTypes = [] } = useGetConnectionTypesQuery();
  const [fetchSchema, { data: schemaResp }] =
    useLazyGetConnectionConfigSchemaByTypeQuery();
  const [createConnection, { isLoading: creating }] =
    useCreateConnectionMutation();
  const [createProvider, { isLoading: creatingProvider }] =
    useCreateProviderMutation();
  const [createSecret, { isLoading: creatingSecret }] =
    useCreateSecretMutation();

  const [providerChoice, setProviderChoice] = useState<string>("");
  const [newProviderName, setNewProviderName] = useState("");
  const [typeDraft, setTypeDraft] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [configDraft, setConfigDraft] = useState<Json>({});
  const [rawDraft, setRawDraft] = useState("{}");
  const [rawError, setRawError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [groupChoice, setGroupChoice] = useState<Record<number, string>>({});
  const [secretDrafts, setSecretDrafts] = useState<Record<string, Json>>({});
  // Set once the connection row exists so a failed secret write can be
  // retried without creating a duplicate connection.
  const [createdConnectionId, setCreatedConnectionId] = useState<number | null>(
    null,
  );

  const newProviderMode = providerChoice === NEW_PROVIDER_VALUE;
  const providerId = newProviderMode ? null : Number(providerChoice) || null;
  const effectiveProviderSlug =
    providers.find((p) => p.id === providerId)?.slug ?? "";

  useEffect(() => {
    if (!open) {
      setProviderChoice("");
      setNewProviderName("");
      setTypeDraft("");
      setNameDraft("");
      setConfigDraft({});
      setRawDraft("{}");
      setRawError(null);
      setCreateError(null);
      setGroupChoice({});
      setSecretDrafts({});
      setCreatedConnectionId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !typeDraft) return;
    fetchSchema({ provider: effectiveProviderSlug, type: typeDraft });
  }, [open, typeDraft, effectiveProviderSlug, fetchSchema]);

  const configSchema = (schemaResp?.config_schema as Json | null) ?? null;

  const { data: slotInfo } = useGetConnectionCredentialTypesByTypeQuery(
    { provider: effectiveProviderSlug, type: typeDraft },
    { skip: !open || !typeDraft },
  );
  const requiredGroups =
    slotInfo?.adapter_registered === true ? slotInfo.required_groups : [];
  const slotByType = useMemo(() => {
    const map: Record<string, { label: string; secret_schema: Json | null }> =
      {};
    (slotInfo?.credential_types ?? []).forEach((slot) => {
      map[slot.type] = {
        label: slot.label,
        secret_schema: slot.secret_schema as Json | null,
      };
    });
    return map;
  }, [slotInfo]);

  // Different provider/type = different secret shapes; drop stale drafts.
  useEffect(() => {
    setSecretDrafts({});
    setGroupChoice({});
  }, [effectiveProviderSlug, typeDraft]);

  const canSubmit =
    createdConnectionId != null ||
    (typeDraft !== "" &&
      (newProviderMode ? newProviderName.trim() !== "" : providerId != null));

  const handleCreate = async () => {
    let config: Json = configDraft;
    if (!configSchema) {
      try {
        config = rawDraft.trim() ? JSON.parse(rawDraft) : {};
      } catch {
        setRawError("Invalid JSON.");
        return;
      }
    }
    try {
      let connectionId = createdConnectionId;
      if (connectionId == null) {
        let effectiveProviderId = providerId as number;
        if (newProviderMode) {
          const provider = await createProvider({
            name: newProviderName.trim(),
          }).unwrap();
          effectiveProviderId = provider.id;
        }
        const created = await createConnection({
          client: clientId,
          provider: effectiveProviderId,
          type: typeDraft,
          ...(nameDraft.trim() ? { name: nameDraft.trim() } : {}),
          config,
        }).unwrap();
        connectionId = created.id;
        setCreatedConnectionId(created.id);
      }

      const failures: string[] = [];
      let secretsSaved = 0;
      for (let index = 0; index < requiredGroups.length; index += 1) {
        const group = requiredGroups[index];
        const chosenType = groupChoice[index] ?? group[0];
        const draft = secretDrafts[chosenType];
        const hasValues =
          draft &&
          Object.values(draft).some((value) => value !== "" && value != null);
        if (!hasValues) continue;
        try {
          await createSecret({
            connectionId,
            type: chosenType,
            secret: draft,
          }).unwrap();
          secretsSaved += 1;
          setSecretDrafts((m) => ({ ...m, [chosenType]: {} }));
        } catch (err) {
          failures.push(
            formatApiError(
              (err as { data?: unknown })?.data,
              `Could not save the ${chosenType} credential.`,
            ),
          );
        }
      }

      if (failures.length) {
        setCreateError(
          "Connection created, but some credentials failed: " +
            failures.join(" | ") +
            " Fix the values and press Create again, or finish later in " +
            "Settings.",
        );
        return;
      }

      dispatch(
        pushNotification({
          severity: "success",
          message: secretsSaved
            ? "Connection and credentials created."
            : "Connection created. Credentials can be added on its card.",
        }),
      );
      onCreated?.(connectionId);
      onClose();
    } catch (err) {
      const data = (err as { data?: Record<string, unknown> })?.data;
      setCreateError(formatApiError(data, "Could not create the connection."));
    }
  };

  const busy = creating || creatingProvider || creatingSecret;

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title="New connection"
      maxWidth="sm"
      showCloseIcon
      actions={
        <>
          <CustomButton fullWidth={false} onClick={onClose}>
            Cancel
          </CustomButton>
          <CustomButton
            fullWidth={false}
            onClick={handleCreate}
            disabled={!canSubmit || busy}
          >
            {createdConnectionId != null ? "Retry credentials" : "Create"}
          </CustomButton>
        </>
      }
    >
      <Stack spacing={2}>
        {createError && <Alert severity="error">{createError}</Alert>}

        <TextField
          select
          size="small"
          fullWidth
          label="Provider"
          value={providerChoice}
          disabled={createdConnectionId != null}
          onChange={(e) => setProviderChoice(e.target.value)}
        >
          {providers.map((provider) => (
            <MenuItem key={provider.id} value={String(provider.id)}>
              {provider.name}
            </MenuItem>
          ))}
          <MenuItem value={NEW_PROVIDER_VALUE}>New provider...</MenuItem>
        </TextField>
        {newProviderMode && (
          <TextField
            size="small"
            fullWidth
            label="New provider name"
            value={newProviderName}
            disabled={createdConnectionId != null}
            onChange={(e) => setNewProviderName(e.target.value)}
          />
        )}

        <TextField
          select
          size="small"
          fullWidth
          label="Type"
          value={typeDraft}
          disabled={createdConnectionId != null}
          onChange={(e) => setTypeDraft(e.target.value)}
        >
          {connectionTypes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          size="small"
          fullWidth
          label="Name (optional)"
          helperText="Distinguishes sibling connections of the same provider (e.g. 'Outbound SFTP')."
          value={nameDraft}
          disabled={createdConnectionId != null}
          onChange={(e) => setNameDraft(e.target.value)}
        />

        {typeDraft &&
          (configSchema ? (
            <JsonSchemaForm
              schema={configSchema}
              value={configDraft}
              onChange={setConfigDraft}
              disabled={createdConnectionId != null}
            />
          ) : (
            <JsonCodeField
              value={rawDraft}
              error={rawError}
              rows={5}
              onChange={(v) => {
                setRawDraft(v);
                setRawError(null);
              }}
            />
          ))}

        {requiredGroups.length > 0 && (
          <>
            <Divider />
            <Typography variant="body2" fontWeight={600}>
              Credentials
            </Typography>
            {requiredGroups.map((group, index) => {
              const chosenType = groupChoice[index] ?? group[0];
              const slot = slotByType[chosenType];
              return (
                <Stack key={group.join("|")} spacing={1}>
                  {group.length > 1 && (
                    <TextField
                      select
                      size="small"
                      fullWidth
                      label="Auth method"
                      value={chosenType}
                      onChange={(e) =>
                        setGroupChoice((m) => ({
                          ...m,
                          [index]: e.target.value,
                        }))
                      }
                    >
                      {group.map((type) => (
                        <MenuItem key={type} value={type}>
                          {slotByType[type]?.label ?? humanizeKey(type)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                  {slot?.secret_schema ? (
                    <JsonSchemaForm
                      schema={slot.secret_schema}
                      value={secretDrafts[chosenType] ?? {}}
                      onChange={(next) =>
                        setSecretDrafts((m) => ({ ...m, [chosenType]: next }))
                      }
                    />
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      No secret fields for {chosenType}.
                    </Typography>
                  )}
                </Stack>
              );
            })}
            <Typography variant="caption" color="text.secondary">
              Leave the credential fields empty to configure them later on the
              connection card.
            </Typography>
          </>
        )}
      </Stack>
    </AppDialog>
  );
}
